import clsx from 'clsx';
import React, { Activity, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useOnClickOutside } from 'usehooks-ts';

import { groupBy, IDropdownOptionV2, shortenLabel } from '@dl/declarations';
import { INPUT_SIZES, INPUT_VARIANTS, POST_SET_VALUE_OPTIONS } from '../../utils/constants';
import { mapLookupsToOptions } from '../../utils/helpers';
import { getQuery } from '../../utils/queries';
import { Chip } from '../ui/Chip';
import { CancelButton, FormDescription, FormError } from './shared';

import { IoIosArrowDown } from "react-icons/io";
import { Loader } from '../ui/Loader';

const MIN_CHAR_COUNT_FOR_SEARCH = 2
const MAX_CHIPS_COUNT = 5

const noOfColumns = {
    "1": "grid-cols-1",
    "2": "grid-cols-2",
    "3": "grid-cols-3",
    "4": "grid-cols-4",
    "5": "grid-cols-5",
} as const

interface DropdownOptionProps extends Pick<DropdownProps, "labelKey" | "customRenderLabel"> {
    option: IDropdownOptionV2;
    isSelected?: boolean;
    handleOptionClick?: (e: React.MouseEvent, option: IDropdownOptionV2) => void
}

const DropdownOption: React.FC<DropdownOptionProps> = (props) => {
    const {
        option,
        isSelected,
        handleOptionClick,
        labelKey = "label",
        customRenderLabel,
    } = props;

    const handleClick = (e: React.MouseEvent) => {
        if (handleOptionClick) {
            handleOptionClick(e, option)
        }
    }

    return (
        <button
            className={clsx(
                "flex justify-between items-center",
                "w-full cursor-pointer block px-2 py-1 text-[14px] rounded font-semibold",
                isSelected ? "text-cs-500" : "text-cs-700 hover:bg-cs-300"
            )}
            type='button'
            role="menuitem"
            onClick={(e) => handleClick(e)}
        >
            {
                customRenderLabel ? (
                    customRenderLabel(option)
                ) : (
                    <span className='whitespace-nowrap'>
                        {option.preLabel}
                        <span
                            dangerouslySetInnerHTML={{ __html: option[labelKey] }}
                        >
                        </span>
                        {option.postLabel}
                    </span>
                )
            }
        </button>
    )
}

type WithOptions = {
    options?: Array<IDropdownOptionV2>
    fixedOptions?: Array<IDropdownOptionV2>;
    staticOptions?: Array<IDropdownOptionV2>;
}

type WithOptionsLoader = {
    optionsQueryKey?: Parameters<typeof getQuery>[0]
    optionsQueryParams?: Record<string, string>;
}

type DropdownProps = {
    as?: 'with-options' | 'with-options-loader';
    id?: string;
    label?: string;
    description?: string;
    required?: boolean;
    value: string;
    handleChange: (option: IDropdownOptionV2) => void;
    allowMultiSelect?: boolean;
    allowSingleDeselect?: boolean;
    maxChipsCount?: number;
    searchOptions?: {
        allow: boolean;
        label?: string;
    }
    distributionOptions?: {
        noOfColumns: keyof typeof noOfColumns
    }

    groupKey?: string;
    valueKey?: keyof Pick<IDropdownOptionV2, "_id" | "slug">;
    labelKey?: keyof Pick<IDropdownOptionV2, "label" | "slug">;
    shouldShortenLabel?: boolean;
    labelWrapperClassName?: string;
    customRenderLabel?: (option: IDropdownOptionV2) => React.ReactNode;
    hasError?: boolean;
    disabled?: boolean;
    inputSize?: keyof typeof INPUT_SIZES;
    menuSize?: keyof typeof INPUT_SIZES;
    inputVariant?: keyof typeof INPUT_VARIANTS;
} & (WithOptions & WithOptionsLoader)

export const Dropdown: React.FC<DropdownProps> = (props) => {
    const {
        as = 'with-options',
        options = [],
        fixedOptions = [],
        staticOptions = [],
        optionsQueryKey = "none",
        optionsQueryParams = {},
        label = '',
        description,
        required = false,
        value,
        handleChange,
        allowMultiSelect = false,
        allowSingleDeselect = true,
        maxChipsCount = MAX_CHIPS_COUNT,
        distributionOptions = {
            noOfColumns: "1"
        },
        valueKey = "slug",
        labelKey = "label",
        customRenderLabel,
        hasError = false,
        disabled = false,
        inputSize = "fit",
        menuSize = "sm",
        inputVariant = "gray_text_white_bg",
        shouldShortenLabel = false,
        labelWrapperClassName,
        searchOptions = {
            allow: true,
            label: ''
        }
    } = props

    const labelRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [debouncedInputValue, setDebouncedInputValue] = useState(inputValue);
    const [usedOptions, setUsedOptions] = useState<Record<string, IDropdownOptionV2[]>>({});
    const [filteredOptions, setFilteredOptions] = useState<Record<string, IDropdownOptionV2[]>>({});
    const [selectedOptions, setSelectedOptions] = useState<IDropdownOptionV2[]>([]);
    const [menuPosition, setMenuPosition] = useState<'left' | 'right'>('left');

    const optionsQuery = getQuery(optionsQueryKey);

    const { data: fetchedOptions } = optionsQuery({
        query: debouncedInputValue,
        ...optionsQueryParams
    }, {
        enabled: (optionsQueryKey !== 'none' && debouncedInputValue.length >= MIN_CHAR_COUNT_FOR_SEARCH)
    })

    const { data: initialOptions, isLoading: isInitialOptionsLoading } = optionsQuery({
        [valueKey]: value,
        limit: String(value?.split(',')?.length || 10)
    }, {
        enabled: (optionsQueryKey !== 'none' && value.length > 0),
        // fetchOnce: true,
    })

    const groupedOptions = useMemo(() => {
        return groupBy(options, (item) => item.groupKey);
    }, [options])

    const selectedOptionsMap = useMemo(() => {
        const map = new Map<string, IDropdownOptionV2>();

        selectedOptions.forEach((option) => {
            map.set(option[valueKey], option);
        });

        return map;
    }, [selectedOptions])

    const showDropdown = useMemo(() => {
        return isDropdownOpen && (selectedOptions.length > 0 || Object.entries(filteredOptions).length > 0);
    }, [isDropdownOpen, selectedOptions, filteredOptions])

    useLayoutEffect(() => {
        if (showDropdown && labelRef.current) {
            const rect = labelRef.current.getBoundingClientRect();

            const newPosition = ((rect.x / window.innerWidth) > 0.75) ? 'right' : 'left';
            setMenuPosition(newPosition);
        }
    }, [showDropdown])

    const toggleDropdown = (state: boolean) => {
        if (disabled) {
            if (isDropdownOpen) {
                setIsDropdownOpen(false)
            }

            return
        }

        setIsDropdownOpen(state);
    };

    const handleOptionClick = (e: React.MouseEvent, option: IDropdownOptionV2) => {
        e.stopPropagation();

        const values = value.split(',').map(v => v?.trim());
        const isOptionSelected = selectedOptionsMap.has(option[valueKey]);

        let newValues: string[] = []

        if (allowMultiSelect) {
            if (isOptionSelected) {
                newValues = (values
                    .filter(v => v !== option[valueKey])
                    .filter((v) => v?.trim())
                    .filter(Boolean))

                setSelectedOptions((prevValue) => prevValue.filter(v => v[valueKey] !== option[valueKey]));
            }
            else {
                newValues = ([...values, option[valueKey]]
                    .filter((v) => v?.trim())
                    .filter(Boolean));

                setSelectedOptions((prevValue) => [...prevValue, option])
            }
        }
        else {
            newValues = isOptionSelected ? [...(allowSingleDeselect ? [] : [option[valueKey]])] : [option[valueKey]];
            setSelectedOptions(() => isOptionSelected ? [...(allowSingleDeselect ? [] : [option])] : [option])
        }


        handleChange({
            _id: newValues.join(','),
            slug: newValues.join(','),
            label: newValues.join(','),
            groupKey: ''
        });

        if (!allowMultiSelect) {
            setIsDropdownOpen(false);
        }

        if (inputValue) {
            setInputValue('')
        }
    }

    const renderOption = (option: IDropdownOptionV2) => {
        const values = value.split(',').map(v => v?.trim());
        const isOptionSelected = values.includes(option[valueKey]);

        return (
            <DropdownOption
                key={option[valueKey]}
                option={option}
                labelKey={labelKey}
                customRenderLabel={customRenderLabel}
                isSelected={isOptionSelected}
                handleOptionClick={handleOptionClick}
            />
        )
    }

    const renderValue = () => {
        const usedSelectedOptions = [...staticOptions, ...selectedOptions];

        if (usedSelectedOptions.length > 0) {
            if (!allowMultiSelect && usedSelectedOptions[0]) {
                return (
                    <span
                        className='me-auto truncate ...'
                        dangerouslySetInnerHTML={{
                            __html: usedSelectedOptions[0][labelKey]
                        }}
                    >
                    </span>
                )
            }

            return (
                <>
                    {(usedSelectedOptions.slice(0, maxChipsCount)).map((option) => {
                        if (option) {
                            return (
                                <Chip
                                    key={option?._id}
                                    rightIcon={(
                                        <CancelButton
                                            size={20}
                                            onClick={(e) => handleOptionClick(e, option)}
                                        />
                                    )}
                                    variant='blue_text_white_bg'
                                >
                                    <span className='truncate ...'>
                                        {option.preLabel}
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    shouldShortenLabel ? shortenLabel(option[labelKey], 15) : option[labelKey]
                                            }}
                                        />
                                        {option.postLabel}
                                    </span>
                                </Chip>
                            )
                        }

                        return null;
                    })}

                    {usedSelectedOptions.length > maxChipsCount && (
                        <span className='text-cs-normal-blue'>
                            +{usedSelectedOptions.length - maxChipsCount}
                        </span>
                    )}
                </>
            )
        }

        if (searchOptions.allow) {
            return null;
        }

        return (
            <span>
                {label}
            </span>
        )
    };

    const renderLabel = () => {
        if (isInitialOptionsLoading) {
            return <Loader loaderSize='sm' />
        }

        return (
            <>
                <div className='flex items-center gap-1'>
                    {renderValue()}

                    {
                        searchOptions.allow && (
                            <input
                                type='text'
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={selectedOptions.length > 0 ? "" : label}
                                className={clsx(
                                    "outline-none bg-transparent p-1",
                                )}
                                disabled={disabled}
                            />
                        )
                    }
                </div>

                <IoIosArrowDown
                    size={18}
                    className={clsx(
                        'cursor-pointer fill-current',
                        // 'ml-2 pl-2 border-l'
                    )}
                />
            </>
        )
    }

    useEffect(() => {
        setFilteredOptions(usedOptions)
    }, [usedOptions])

    /** WITH-OPTIONS effects */

    useEffect(() => {
        if (as !== 'with-options') return
        setUsedOptions(groupedOptions)
    }, [as, groupedOptions])

    useEffect(() => {
        if (as !== 'with-options') return

        const timerId = setTimeout(() => {
            const filteredOptions = Object.entries(usedOptions).reduce((acc, [key, options]) => {
                const filteredSubOptions = options.filter((option) =>
                    option[labelKey].toLowerCase().includes(inputValue.toLowerCase())
                );

                if (filteredSubOptions.length > 0) {
                    acc[key] = filteredSubOptions;
                }

                return acc;
            }, {} as Record<string, IDropdownOptionV2[]>);

            setFilteredOptions(filteredOptions);
        }, 250);

        return () => {
            clearTimeout(timerId);
        }
    }, [as, usedOptions, inputValue]);

    useEffect(() => {
        if (as !== 'with-options') return

        const values = value.split(',').map(v => v?.trim());
        const selectedOptions = Object.entries(usedOptions).reduce((acc, [_key, options]) => {
            const selectedSubOptions = options.filter((option) =>
                values.includes(option[valueKey])
            );

            return acc.concat(selectedSubOptions);
        }, [] as IDropdownOptionV2[]);

        setSelectedOptions(selectedOptions);
    }, [as, value, usedOptions])

    /** WITH-OPTIONS-LOADER effects */

    useEffect(() => {
        if (as !== 'with-options-loader') return

        const timerId = setTimeout(() => {
            setDebouncedInputValue(inputValue)
        }, 500);

        return () => {
            clearTimeout(timerId);
        }
    }, [as, inputValue])

    useEffect(() => {
        if (as !== 'with-options-loader') return

        const mapped = mapLookupsToOptions(fetchedOptions || [], "name", () => "Options")
        const grouped = groupBy(mapped, item => item.groupKey)
        setUsedOptions(grouped);
    }, [as, fetchedOptions])

    useEffect(() => {
        if (as !== 'with-options-loader') return

        const mapped = mapLookupsToOptions(initialOptions || [], "name", () => "Options")

        setSelectedOptions(mapped)
    }, [as, initialOptions])

    useOnClickOutside(dropdownRef as React.RefObject<HTMLElement>, () => toggleDropdown(!isDropdownOpen))

    return (
        <div
            className={clsx(
                showDropdown && "z-dropdown",
                "relative inline-block text-left"
            )}
        >
            {description && <FormDescription description={description} required={required} />}

            <div
                className={clsx(
                    "h-10 flex items-center justify-between gap-x-4 px-2 py-1 text-[14px] rounded",
                    // searchOptions.allow ? "cursor-text" : "cursor-pointer",
                    disabled && "cursor-not-allowed",
                    INPUT_SIZES[inputSize],
                    INPUT_VARIANTS[inputVariant],
                    INPUT_VARIANTS['common'],
                    hasError && "border-red-500",
                    labelWrapperClassName
                )}
                onClick={() => toggleDropdown(true)}
                ref={labelRef}
            >
                {renderLabel()}
            </div>

            <Activity
                mode={showDropdown ? 'visible' : 'hidden'}
            >
                <div
                    className={clsx(
                        "origin-top-right",
                        "absolute p-1 mt-2 rounded shadow bg-white",
                        menuPosition === 'left' ? 'left-0' : 'right-0',
                        INPUT_SIZES[menuSize],
                    )}
                    ref={dropdownRef}
                    role='menu'
                >
                    {(as === 'with-options-loader' && selectedOptions.length > 0) && (
                        <div>
                            {selectedOptions.map((option) => (
                                <DropdownOption
                                    key={option[valueKey]}
                                    option={option}
                                    labelKey={labelKey}
                                    customRenderLabel={customRenderLabel}
                                    isSelected
                                    handleOptionClick={handleOptionClick}
                                />
                            ))}
                            <hr className='my-1' />
                        </div>
                    )}

                    {fixedOptions.length > 0 && (
                        <div
                            className={clsx('min-w-max grid grid-cols-1')}
                        >
                            {fixedOptions.map((option) => (
                                <DropdownOption
                                    key={option[valueKey]}
                                    option={option}
                                    labelKey={labelKey}
                                    customRenderLabel={customRenderLabel}
                                    handleOptionClick={handleOptionClick}
                                />
                            ))}
                            <hr className='my-1 col-span-full' />
                        </div>
                    )}

                    <div
                        className={clsx(
                            'grid max-h-52 overflow-y-scroll',
                            noOfColumns[distributionOptions.noOfColumns]
                        )}
                    >
                        {Object.entries(filteredOptions).map(([key, options], _, array) => (
                            <div key={key}>
                                {
                                    array.length > 1 && !inputValue && (
                                        <div className='px-2 first-letter:uppercase font-bold'>
                                            {key}
                                        </div>
                                    )
                                }
                                {options.map(renderOption)}
                            </div>
                        ))}
                    </div>
                </div>
            </Activity>
        </div>
    );
};

interface ControlledDropdownProps extends Omit<DropdownProps, "value" | "handleChange"> {
    name: string;
    errorOptions?: {
        show: boolean;
        extra_paths: string[];
    }
}

export const ControlledDropdown: React.FC<ControlledDropdownProps> = (props) => {
    if (props.allowMultiSelect) {
        return <ControlledMultiDropdown {...props} />
    }

    return <ControlledSingleDropdown {...props} />
}

const ControlledSingleDropdown: React.FC<ControlledDropdownProps> = (props) => {
    const {
        name,
        valueKey = "slug",
        labelKey = "label",
        customRenderLabel,
        errorOptions = {
            show: false,
            extra_paths: []
        },
        ...restOfProps
    } = props;

    const formContext = useFormContext();

    const { control, formState } = formContext;

    const handleChange = (option: IDropdownOptionV2) => {
        const optionValue = option[valueKey] || '';

        formContext.setValue(name, optionValue, POST_SET_VALUE_OPTIONS);
    }

    const hasError = useMemo(() => {
        if (Boolean(formState?.errors?.[name])) {
            return true;
        }

        if (errorOptions.extra_paths.length > 0) {
            const extraErrors = Object.values(formState.errors)
                .filter((extraError) => extraError && errorOptions.extra_paths.includes(extraError.type as string));

            return extraErrors.length > 0;
        }

        return false;
    }, [formState.errors, name, errorOptions.extra_paths])

    return (
        <div className='flex flex-col justify-start'>
            <Controller
                name={name}
                control={control}
                render={({ field: { ref, ...restOfField } }) => {
                    return (
                        <Dropdown
                            {...restOfField}
                            {...restOfProps}
                            valueKey={valueKey}
                            labelKey={labelKey}
                            customRenderLabel={customRenderLabel}
                            handleChange={handleChange}
                            value={(restOfField.value || '').trim()}
                            hasError={hasError}
                        />
                    )
                }}
            />

            {errorOptions.show && (<FormError error={formState?.errors?.[name]?.message as string} />)}
        </div>
    )
}

const ControlledMultiDropdown: React.FC<ControlledDropdownProps> = (props) => {
    const {
        name,
        valueKey = "slug",
        labelKey = "label",
        customRenderLabel,
        errorOptions = {
            show: false,
            extra_paths: []
        },
        ...restOfProps
    } = props;

    const formContext = useFormContext();

    const { control, formState } = formContext;

    const {
        replace
    } = useFieldArray({
        control,
        name
    })

    const getJoinedValue = (value: string | string[] | undefined) => {
        if (Array.isArray(value)) {
            return value.map(v => v?.toString()?.trim()).filter(Boolean).join(',');
        }

        return ''
    }

    const handleChange = (option: IDropdownOptionV2) => {
        const optionValue = option[valueKey] || '';

        const optionsSeparatedValues = optionValue.split(',').map(v => v?.trim());
        replace(optionsSeparatedValues);
    }

    const hasError = useMemo(() => {
        if (Boolean(formState?.errors?.[name])) {
            return true;
        }

        if (errorOptions.extra_paths.length > 0) {
            const extraErrors = Object.values(formState.errors)
                .filter((extraError) => extraError && errorOptions.extra_paths.includes(extraError.type as string));

            return extraErrors.length > 0;
        }

        return false;
    }, [formState.errors, name, errorOptions.extra_paths])

    return (
        <div className='flex flex-col justify-start'>
            <Controller
                name={name}
                control={control}
                render={({ field: { ref, ...restOfField } }) => {
                    return (
                        <Dropdown
                            {...restOfField}
                            {...restOfProps}
                            valueKey={valueKey}
                            labelKey={labelKey}
                            customRenderLabel={customRenderLabel}
                            handleChange={handleChange}
                            value={getJoinedValue(restOfField.value)}
                            hasError={hasError}
                        />
                    )
                }}
            />

            {errorOptions.show && (<FormError error={formState?.errors?.[name]?.message as string} />)}
        </div>
    )
}
